/**
 * Servicio de almacenamiento local (localStorage)
 * 
 * Proporciona interfaz limpia para almacenar/recuperar datos
 * con manejo automático de serialización JSON
 */

class StorageService {
  /**
   * Guardar valor en localStorage
   * @param {string} key - Clave
   * @param {*} value - Valor (se serializa a JSON)
   * @returns {boolean} true si tuvo éxito
   */
  static set(key, value) {
    try {
      const serialized = JSON.stringify(value);
      localStorage.setItem(key, serialized);
      return true;
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        console.error('✗ Cuota de localStorage excedida');
      } else {
        console.error('✗ Error guardando en localStorage:', error);
      }
      return false;
    }
  }

  /**
   * Obtener valor desde localStorage
   * @param {string} key - Clave
   * @param {*} defaultValue - Valor por defecto si no existe
   * @returns {*} Valor recuperado o defaultValue
   */
  static get(key, defaultValue = null) {
    try {
      const stored = localStorage.getItem(key);
      if (stored === null) {
        return defaultValue;
      }
      return JSON.parse(stored);
    } catch (error) {
      console.error(`✗ Error obteniendo "${key}" de localStorage:`, error);
      return defaultValue;
    }
  }

  /**
   * Remover valor de localStorage
   * @param {string} key - Clave
   * @returns {boolean} true si tuvo éxito
   */
  static remove(key) {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('✗ Error removiendo de localStorage:', error);
      return false;
    }
  }

  /**
   * Limpiar todo localStorage
   * @returns {boolean} true si tuvo éxito
   */
  static clear() {
    try {
      localStorage.clear();
      return true;
    } catch (error) {
      console.error('✗ Error limpiando localStorage:', error);
      return false;
    }
  }

  /**
   * Verificar si una clave existe
   * @param {string} key - Clave
   * @returns {boolean}
   */
  static has(key) {
    return localStorage.getItem(key) !== null;
  }

  /**
   * Obtener todas las claves
   * @returns {string[]} Array de claves
   */
  static keys() {
    return Object.keys(localStorage);
  }

  /**
   * Obtener tamaño de localStorage (aproximado en caracteres)
   * @returns {number} Tamaño en caracteres
   */
  static getSize() {
    let totalSize = 0;
    for (const key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        totalSize += localStorage[key].length + key.length;
      }
    }
    return totalSize;
  }
}

export default StorageService;
